import { devLog, devError } from '../utils/devLogger';
import React, { useEffect, useMemo, useState } from 'react';
import { Card, Row, Col, Form, Button, Table, Badge, Alert } from 'react-bootstrap';
import { collection, addDoc, getDocs, orderBy, query, where, serverTimestamp, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../auth/AuthContext';

const DailyReportLog = () => {
  const { currentUser, userProfile } = useAuth();
  const [reports, setReports] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    dateTime: '',
    cohortId: '',
    reportText: ''
  });
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [removeAttachment, setRemoveAttachment] = useState(false);
  const [editingId, setEditingId] = useState('');

  const isAdmin = userProfile?.role === 'admin';
  const isQA = userProfile?.role === 'qa';
  const canViewAll = isAdmin || isQA;

  const loadCohorts = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'cohorts'));
      let items = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }));
      if (!isAdmin && !isQA) {
        if (userProfile?.assignedCohorts?.length) {
          items = items.filter((cohort) => userProfile.assignedCohorts.includes(cohort.id));
        } else if (userProfile?.assignedPrograms?.length) {
          items = items.filter((cohort) => userProfile.assignedPrograms.includes(cohort.programId));
        }
      }
      items.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCohorts(items);
    } catch (err) {
      devError('DailyReportLog: failed to load cohorts', err);
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      setError('');
      const baseRef = collection(db, 'dailyReports');
      let snapshot;
      if (canViewAll) {
        snapshot = await getDocs(query(baseRef, orderBy('dateTime', 'desc')));
      } else {
        // Avoid requiring a composite index (createdById + dateTime) for facilitator/external views.
        snapshot = await getDocs(query(baseRef, where('createdById', '==', currentUser?.uid || '')));
      }

      const items = snapshot.docs.map((docSnap) => {
        const rawDate = docSnap.data().dateTime;
        const parsedDate = rawDate?.toDate ? rawDate.toDate() : new Date(rawDate);
        return {
          id: docSnap.id,
          ...docSnap.data(),
          dateTime: parsedDate
        };
      }).sort((a, b) => {
        const aTime = a.dateTime instanceof Date && !Number.isNaN(a.dateTime.getTime()) ? a.dateTime.getTime() : 0;
        const bTime = b.dateTime instanceof Date && !Number.isNaN(b.dateTime.getTime()) ? b.dateTime.getTime() : 0;
        return bTime - aTime;
      });
      setReports(items);
    } catch (err) {
      devError('DailyReportLog: failed to load reports', err);
      setError('Failed to load reports.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      loadCohorts();
      loadReports();
    }
  }, [currentUser, canViewAll]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!form.dateTime || !form.cohortId || !form.reportText.trim()) {
      setError('Please fill in date/time, cohort, and report text.');
      return;
    }

    const cohort = cohorts.find((c) => c.id === form.cohortId);
    const payload = {
      dateTime: new Date(form.dateTime),
      cohortId: form.cohortId,
      cohortName: cohort?.name || form.cohortId,
      reportText: form.reportText.trim(),
      createdById: currentUser?.uid || '',
      createdByName: userProfile?.displayName || currentUser?.email || 'Unknown',
      updatedAt: serverTimestamp(),
      createdAt: serverTimestamp()
    };

    try {
      const uploadAttachment = async (reportId) => {
        if (!attachmentFile) return null;
        const safeName = attachmentFile.name.replace(/[^\w.\-]+/g, '_');
        const path = `dailyReports/${reportId}/${safeName}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, attachmentFile);
        const url = await getDownloadURL(storageRef);
        return {
          attachmentName: attachmentFile.name,
          attachmentUrl: url,
          attachmentPath: path
        };
      };

      if (editingId) {
        const attachment = await uploadAttachment(editingId);
        await updateDoc(doc(db, 'dailyReports', editingId), {
          dateTime: payload.dateTime,
          cohortId: payload.cohortId,
          cohortName: payload.cohortName,
          reportText: payload.reportText,
          ...(attachment || {}),
          ...(removeAttachment
            ? { attachmentName: null, attachmentUrl: null, attachmentPath: null }
            : {}),
          updatedAt: serverTimestamp()
        });
        setSuccess('Report updated.');
      } else {
        const reportRef = await addDoc(collection(db, 'dailyReports'), payload);
        const attachment = await uploadAttachment(reportRef.id);
        if (attachment) {
          await updateDoc(doc(db, 'dailyReports', reportRef.id), {
            ...attachment,
            updatedAt: serverTimestamp()
          });
        }
        setSuccess('Report added.');
      }
      setForm({ dateTime: '', cohortId: '', reportText: '' });
      setAttachmentFile(null);
      setRemoveAttachment(false);
      setEditingId('');
      await loadReports();
    } catch (err) {
      devError('DailyReportLog: failed to save report', err);
      setError('Failed to save report.');
    }
  };

  const handleEdit = (report) => {
    setEditingId(report.id);
    const localValue = report.dateTime instanceof Date
      ? new Date(report.dateTime.getTime() - report.dateTime.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
      : '';
    setForm({
      dateTime: localValue,
      cohortId: report.cohortId || '',
      reportText: report.reportText || ''
    });
    setAttachmentFile(null);
    setRemoveAttachment(false);
  };

  const handleDelete = async (reportId) => {
    if (!window.confirm('Delete this report entry?')) return;
    try {
      await deleteDoc(doc(db, 'dailyReports', reportId));
      await loadReports();
    } catch (err) {
      devError('DailyReportLog: failed to delete', err);
      setError('Failed to delete report.');
    }
  };

  const canEdit = (report) => isAdmin || report.createdById === currentUser?.uid;

  const reportRows = useMemo(() => reports, [reports]);

  return (
    <Card className="shadow-sm border-0">
      <Card.Header className="bg-white">
        <div>
          <h5 className="mb-0">Daily Reports</h5>
          <small className="text-muted">Log date, cohort, and a short daily report.</small>
        </div>
      </Card.Header>
      <Card.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {success && <Alert variant="success">{success}</Alert>}

        <Form onSubmit={handleSubmit} className="mb-4">
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Date & Time</Form.Label>
                <Form.Control
                  type="datetime-local"
                  value={form.dateTime}
                  onChange={(e) => setForm((prev) => ({ ...prev, dateTime: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Cohort</Form.Label>
                <Form.Select
                  value={form.cohortId}
                  onChange={(e) => setForm((prev) => ({ ...prev, cohortId: e.target.value }))}
                >
                  <option value="">Select cohort...</option>
                  {cohorts.map((cohort) => (
                    <option key={cohort.id} value={cohort.id}>
                      {cohort.name || cohort.cohortName || cohort.id}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4} className="d-flex align-items-end">
              <Button type="submit" variant="primary" className="w-100">
                {editingId ? 'Update Report' : 'Add Report'}
              </Button>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Report for the Day</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={form.reportText}
                  onChange={(e) => setForm((prev) => ({ ...prev, reportText: e.target.value }))}
                />
              </Form.Group>
            </Col>
            <Col md={12}>
              <Form.Group>
                <Form.Label>Attach Screenshot Document (optional)</Form.Label>
                <Form.Control
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                />
              </Form.Group>
            </Col>
            {editingId && (
              <Col md={12}>
                <Form.Check
                  type="checkbox"
                  id="remove-report-attachment"
                  label="Remove existing attachment"
                  checked={removeAttachment}
                  onChange={(e) => setRemoveAttachment(e.target.checked)}
                />
              </Col>
            )}
          </Row>
        </Form>

        {loading ? (
          <Alert variant="info">Loading reports...</Alert>
        ) : (
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Date & Time</th>
                <th>Group/Cohort</th>
                <th>Report for the Day</th>
                <th>Reporter</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reportRows.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted">
                    No daily reports yet.
                  </td>
                </tr>
              )}
              {reportRows.map((report) => (
                <tr key={report.id}>
                  <td>{report.dateTime ? new Date(report.dateTime).toLocaleString() : 'N/A'}</td>
                  <td>{report.cohortName || report.cohortId || 'N/A'}</td>
                  <td style={{ whiteSpace: 'pre-wrap' }}>{report.reportText}</td>
                  <td>
                    {report.createdByName || 'N/A'}
                    {report.createdById === currentUser?.uid && (
                      <Badge bg="secondary" className="ms-2">You</Badge>
                    )}
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      <Button size="sm" variant="outline-primary" onClick={() => handleEdit(report)} disabled={!canEdit(report)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(report.id)} disabled={!canEdit(report)}>
                        Delete
                      </Button>
                      {report.attachmentUrl && (
                        <Button size="sm" variant="outline-success" href={report.attachmentUrl} target="_blank" rel="noreferrer">
                          View Attachment
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );
};

export default DailyReportLog;
